import React, { useEffect, useState } from "react";
import {
  Stack,
  TextInput,
  PasswordInput,
  Checkbox,
  Select,
  Button,
  Alert,
  Title,
  Text,
} from "@mantine/core";
import { FormDataResponse } from "types/responses.type";

interface DynamicFormProps {
  formData: FormDataResponse;
  setFormData: React.Dispatch<
    React.SetStateAction<FormDataResponse | undefined>
  >;
  onSubmit: (endpoint: string, data: Record<string, any>) => void;
}

const DynamicForm: React.FC<DynamicFormProps> = ({ formData, onSubmit }) => {
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [error, setError] = useState<string | null>(null);
  const [submitable, setSubmitable] = useState(false);

  const handleChange = (name: string, value: any) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    if (
      formData?.inputs.some(
        (i) => i.required && formValues[i.name] === undefined
      )
    ) {
      setError(
        "Please fill in all required fields before submitting the form."
      );
      return;
    }
    if (!formData.endpoint) {
      setError("Form submission endpoint is missing.");
      return;
    }
    setError(null);
    onSubmit(formData.endpoint, formValues);
  };

  useEffect(() => {
    const values: Record<string, any> = {};
    formData?.inputs.forEach((field) => {
      values[field.name] = field.value;
    });
    setFormValues(values);
  }, [formData]);

  return (
    <Stack>
      <Title order={3}>{formData.title ?? "Form"}</Title>
      {formData.inputs.map((input) => {
        switch (input.type) {
          case "text":
          case "email":
            return (
              <TextInput
                key={input.name}
                label={input.caption}
                type={input.type}
                required={input.required}
                value={formValues[input.name] || ""}
                onChange={(e) =>
                  handleChange(input.name, e.currentTarget.value)
                }
              />
            );
          case "password":
            return (
              <Stack gap={0}>
                <Text>Password</Text>
                <PasswordInput
                  key={input.name}
                  label={input.caption}
                  required={input.required}
                  value={formValues[input.name] || ""}
                  onChange={(e) =>
                    handleChange(input.name, e.currentTarget.value)
                  }
                />
              </Stack>
            );
          case "checkbox":
            return (
              <Checkbox
                key={input.name}
                label={input.caption}
                checked={!!formValues[input.name]}
                onChange={(e) =>
                  handleChange(input.name, e.currentTarget.checked)
                }
              />
            );
          case "select":
            return (
              <Select
                key={input.name}
                label={input.caption}
                data={
                  input.options?.map((opt) => ({
                    value: opt.value,
                    label: opt.caption,
                  })) || []
                }
                required={input.required}
                value={formValues[input.name] || ""}
                onChange={(value) => handleChange(input.name, value)}
              />
            );
          case "alert":
            return (
              <Alert key={input.name} title={input.caption} color="blue">
                {input.value}
              </Alert>
            );
          default:
            return null;
        }
      })}
      <Button onClick={handleSubmit}>Submit</Button>
      {error && <Alert color="red">{error}</Alert>}
    </Stack>
  );
};

export default DynamicForm;
